/**
 * Crossfading banner slider with autoplay and dots.
 *
 * Replaces slick-carousel 1.8, which is unmaintained and required jQuery. Only
 * the fade mode was ever used here — no dragging, no variable widths, no
 * responsive breakpoints — so the useful surface is small enough to own.
 */

export interface FadeSliderOptions {
  /** Milliseconds each slide stays visible. */
  interval?: number;
  /** Show clickable dots. */
  dots?: boolean;
  /** Start advancing automatically. */
  autoplay?: boolean;
}

export interface FadeSlider {
  destroy(): void;
}

export function initFadeSlider(
  container: HTMLElement,
  options: FadeSliderOptions = {},
): FadeSlider | null {
  const { interval = 5000, dots = true, autoplay = true } = options;

  const slides = Array.from(container.querySelectorAll<HTMLElement>(':scope > .slide-item'));

  if (slides.length === 0) {
    return null;
  }

  let index = 0;
  let timer: ReturnType<typeof setInterval> | undefined;

  container.classList.add('fade-slider');

  let dotButtons: HTMLButtonElement[] = [];
  let dotBar: HTMLElement | undefined;

  function show(next: number): void {
    index = (next + slides.length) % slides.length;

    slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      // Hide inactive slides from assistive tech, not just visually.
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    dotButtons.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }

  function start(): void {
    // Autoplay runs regardless of the motion preference, matching the slider
    // this replaced. What reduced motion suppresses is the *animation*: the
    // stylesheet drops the opacity transition, so slides swap instantly instead
    // of crossfading. The content still advances, and the dots remain as the
    // manual control.
    if (!autoplay || slides.length < 2) {
      return;
    }

    stop();
    timer = setInterval(() => show(index + 1), interval);
  }

  function stop(): void {
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  }

  if (dots && slides.length > 1) {
    dotBar = document.createElement('div');
    dotBar.className = 'fade-slider-dots';
    dotBar.setAttribute('role', 'tablist');

    dotButtons = slides.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'fade-slider-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Vis bilde ${i + 1} av ${slides.length}`);
      dot.addEventListener('click', () => {
        show(i);
        start();
      });
      dotBar!.appendChild(dot);
      return dot;
    });

    container.appendChild(dotBar);
  }

  show(0);
  start();

  // Pause while the tab is hidden so a backgrounded page is not animating.
  function onVisibilityChange(): void {
    if (document.hidden) {
      stop();
    } else {
      start();
    }
  }

  document.addEventListener('visibilitychange', onVisibilityChange);

  return {
    destroy(): void {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      dotBar?.remove();
      slides.forEach((slide) => {
        slide.classList.remove('is-active');
        slide.removeAttribute('aria-hidden');
      });
    },
  };
}
